import React, { ChangeEventHandler, forwardRef, LegacyRef, useCallback, useState } from 'react';

import { BaseTextField, SupportingText, TextFieldOutlined } from './TextField.styles';
import { TextFieldProps } from './TextField.types';

export const TextField = forwardRef(
  (
    {
      trailingIcon: TrailingIcon,
      length,
      size,
      label,
      id = 'textfield-id',
      required,
      width,
      error,
      supportingText,
      disabled,
      multiline,
      rows,
      wrap,
      cols,
      autoComplete,
      autoFocus,
      form,
      maxLength,
      minLength,
      name,
      readOnly,
      value,
      onChange,
      style,
      ...props
    }: TextFieldProps,
    ref,
  ) => {
    const commonProps = {
      autoComplete,
      autoFocus,
      disabled,
      form,
      maxLength,
      minLength,
      name,
      placeholder: ' ',
      readOnly,
      required,
      value,
    };
    const [isFocused, setIsFocused] = useState(false);

    const handleInputFocus = useCallback(() => {
      setIsFocused(true);
    }, []);
    const handleInputBlur = useCallback(() => {
      setIsFocused(false);
    }, []);

    return (
      <BaseTextField width={width}>
        <TextFieldOutlined
          size={size}
          hasPlaceholder={!!label}
          error={error}
          disabled={disabled}
          rows={rows}
          multiline={multiline}
          isFocused={isFocused}
          style={style}
          data-testid="textfield-outlined-id"
        >
          {multiline ? (
            <textarea
              ref={ref as LegacyRef<HTMLTextAreaElement>}
              id={id}
              rows={rows}
              cols={cols}
              wrap={wrap}
              onChange={onChange as ChangeEventHandler<HTMLTextAreaElement>}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              {...commonProps}
            />
          ) : (
            <input
              ref={ref as LegacyRef<HTMLInputElement>}
              type="text"
              id={id}
              size={length}
              onChange={onChange as ChangeEventHandler<HTMLInputElement>}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              {...commonProps}
              {...props}
            />
          )}

          <label htmlFor={id} id="input-label-id" data-testid="input-label-id">
            {label}
            {required && '*'}
          </label>
          <fieldset aria-hidden="true">
            <legend>
              <span>
                {label}
                {required && '*'}
              </span>
            </legend>
          </fieldset>
          {TrailingIcon && !multiline && <TrailingIcon />}
          {supportingText && (
            <SupportingText error={error} disabled={disabled}>
              {supportingText}
            </SupportingText>
          )}
        </TextFieldOutlined>
      </BaseTextField>
    );
  },
);

TextField.displayName = 'TextField';
